import {useAddNotification} from '@yandex-delivery/blocks'
import {useFormContext, useFormState} from '@yandex-delivery/form'
import {type Optional} from '@yandex-delivery/types'
import {allPass, equals, filter, flow, get, getErrorMessage, keys, pipe} from '@yandex-delivery/utils'
import type React from 'react'
import {useMemo} from 'react'

import {appConfig, i18n} from 'src/shared/config'
import {OrderEventGoal, useFormUserEventHandler, useOrderAvailability} from 'src/shared/hooks'
import type {Order} from 'src/shared/types'
import {formatOfferInterval, formatPrice} from 'src/shared/utils'

import {useScrollToErrorField} from '../hooks/use-scroll-to-error-field'
import {fetchCreateOrder} from '../service/create-order'

export function useSubmitButtonTitle() {
  const {isSubmitting} = useFormState<Order>()

  if (isSubmitting) {
    return i18n('cargo.create.form.ordering', 'Создаём заказ...')
  }

  return i18n('cargo.create.form.order', 'Заказать')
}

export function useSubmitButtonDetails() {
  const {watch} = useFormContext<Order>()

  const selectedOffer = watch('offerFlow.offer')

  if (selectedOffer) {
    return {
      ETA: formatOfferInterval(selectedOffer.tariff, selectedOffer.visitingIntervals).short,
      price: formatPrice(selectedOffer.price, {
        currency: watch('offerFlow.offer.currency') || appConfig.currency,
        country: appConfig.countryCode,
        currencySign: appConfig.currencySign,
      }),
    }
  }

  return {}
}

/**
 * Причина блокировки кнопки "Заказать" из-за ошибок в заполнении формы
 */
function useFormInvalidDisableReason() {
  const {watch} = useFormContext()
  const {isValid} = useFormState<Order>()
  const isBusiness = watch('isBusiness')
  const offerFailureBlocks = watch('offerFlow.offer.offerFailureBlocks')

  if (offerFailureBlocks) {
    // @ts-expect-error сгенерированный тип у блока - unknown
    return offerFailureBlocks.find(block => block.type === 'button')?.subtitle
  }

  if (isValid) {
    return isBusiness
      ? i18n('form.new-tariff-line.submit-button.sub-title', 'После этого откроется личный кабинет для бизнеса')
      : undefined
  }

  return i18n('cargo.create.fill-requirement-fields', 'Заполните обязательные поля')
}

/**
 * Причина блокировки кнопки "Заказать" из-за отсутствия предложений
 */
function useFormEmptyOffersDisabledReason() {
  const {watch, errors, isValid} = useFormContext<Order>()

  const selectedVertical = watch('offerFlow.vertical.name')
  const offers = watch('__settings.__offers')
  const isBusiness = watch('isBusiness')
  const isOnlyOffersErrorLeft = pipe(
    errors,
    allPass([flow(get('offerFlow.offer.id.message'), Boolean), flow(keys, get('length'), equals(1))]),
  )

  // Типизация GetIn (внутри функции get) в данном кейсе возвращает undefined
  // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
  const selectedVerticalOffersCount = pipe(
    offers,
    filter(flow(get('vertical'), equals(selectedVertical))),
    get('length'),
  )

  if (isValid) {
    return isBusiness
      ? i18n('form.new-tariff-line.submit-button.sub-title', 'После этого откроется личный кабинет для бизнеса')
      : undefined
  }

  if (isOnlyOffersErrorLeft && !!selectedVertical && selectedVerticalOffersCount === 0) {
    return i18n('form.new-tariff-line.disable-reason.empty-offers', 'Нет подходящих вариантов для ваших настроек')
  }

  return undefined
}

/**
 * Причина блокировки кнопки "Заказать"
 */
export function useDisableReason() {
  const invalidFormDisableReason = useFormInvalidDisableReason()
  const noOffersDisableReason = useFormEmptyOffersDisabledReason()
  const {error} = useOrderAvailability()
  const orderAvailabilityErrorMessage = error ? getErrorMessage(error) : void 0

  return invalidFormDisableReason || noOffersDisableReason || orderAvailabilityErrorMessage
}

/**
 * Причина блокировки кнопки "Заказать" в мобильной версии
 * На десктоп пока нет макета
 */
export function useDisableReasonMobile() {
  const {watch, errors, isValid} = useFormContext<Order>()
  const {error} = useOrderAvailability()

  const offerFailureBlocks = watch('offerFlow.offer.offerFailureBlocks')
  const selectedVertical = watch('offerFlow.vertical.name')
  const offers = watch('__settings.__offers')
  const isOnlyOffersErrorLeft = pipe(
    errors,
    allPass([flow(get('offerFlow.offer.id.message'), Boolean), flow(keys, get('length'), equals(1))]),
  )

  if (offerFailureBlocks) {
    return offerFailureBlocks.find(block => block.type === 'button')?.subtitle
  }

  // Типизация GetIn (внутри функции get) в данном кейсе возвращает undefined
  // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
  const selectedVerticalOffersCount = pipe(
    offers,
    filter(flow(get('vertical'), equals(selectedVertical))),
    get('length'),
  )

  if (isOnlyOffersErrorLeft && !!selectedVertical && selectedVerticalOffersCount === 0) {
    return i18n('order.form.submit-button.no-offers', 'Нет подходящих вариантов доставки')
  }

  const orderAvailabilityErrorMessage = error ? getErrorMessage(error) : void 0

  if (orderAvailabilityErrorMessage) {
    return orderAvailabilityErrorMessage
  }

  if (isValid) {
    return undefined
  }

  return i18n('order.form.submit-button.unfilled', 'Заполните данные')
}

export function useSubmitHandler(formRef: React.MutableRefObject<Optional<HTMLDivElement>>): VoidFunction {
  const addNotification = useAddNotification()
  const scrollToFirstError = useScrollToErrorField(formRef)
  const {handleSubmit, watch} = useFormContext<Order>()
  const handleReachGoal = useFormUserEventHandler()
  const {startOrderAvailabilityTransition} = useOrderAvailability()
  const verticalName = watch('vertical_name')

  return useMemo<VoidFunction>(
    function submitHandler() {
      function submitFormHandler(formValues: Order) {
        handleReachGoal(OrderEventGoal.SubmitButtonClick)
        const successSubmitHandler = () => {
          handleReachGoal(
            verticalName === 'cargo_intercity'
              ? OrderEventGoal.SuccessOrderCargoIntercityCreate
              : OrderEventGoal.SuccessOrderCreate,
          )
        }

        return fetchCreateOrder(formValues, successSubmitHandler, startOrderAvailabilityTransition).catch(
          addNotification,
        )
      }

      function errorFormHandler() {
        requestAnimationFrame(scrollToFirstError)
      }

      return handleSubmit(submitFormHandler, errorFormHandler)
    },
    [
      handleSubmit,
      handleReachGoal,
      verticalName,
      startOrderAvailabilityTransition,
      addNotification,
      scrollToFirstError,
    ],
  )
}
